// ============================================================================
//  THEME SYSTEM  —  «тема = данные», UI читает токены, не зная о теме
// ----------------------------------------------------------------------------
//  Как это работает:
//   • Тема — это обычный объект (см. THEMES ниже). Можно задать сколько угодно
//     полей; всё, что не задано, ВЫВОДИТСЯ автоматически из базовых цветов
//     (buildTokens). Поэтому минимальная тема (bg/card/text/accent) сразу даёт
//     полный согласованный набор.
//   • buildTokens(spec) → плоская карта CSS-переменных (--app-*). SettingsContext
//     просто проставляет их в :root. Компоненты используют var(--app-*) и НЕ
//     знают, какая тема активна → логика отделена от оформления.
//   • Поле `css` темы — произвольный CSS (кейфреймы, эффекты фона, переопределения).
//     Он инжектится в <style id="theme-css"> только когда тема активна.
//
//  ДОБАВИТЬ ТЕМУ: добавь одну запись в THEMES. Минимум — bg, card, text, accent.
//  ПЕРЕКЛЮЧИТЬ: settings.setTheme('id') (или из Профиля → Оформление).
//  СПИСОК ТОКЕНОВ-КОНТРАКТА — TOKENS ниже.
// ============================================================================

// ── helpers: hex-математика для автогенерации оттенков ──────────────────────
const clamp = (n) => Math.max(0, Math.min(255, Math.round(n)));
function parseHex(h) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(h || '');
  return m ? [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)] : null;
}
const toHex = (r, g, b) => '#' + [r, g, b].map((x) => clamp(x).toString(16).padStart(2, '0')).join('');
// смешать a→b на долю t (0..1). Если не hex — вернуть a без изменений.
export function mix(a, b, t) {
  const A = parseHex(a), B = parseHex(b);
  if (!A || !B) return a;
  return toHex(A[0] + (B[0] - A[0]) * t, A[1] + (B[1] - A[1]) * t, A[2] + (B[2] - A[2]) * t);
}
export const isDark = (h) => { const c = parseHex(h); if (!c) return true; return (0.299 * c[0] + 0.587 * c[1] + 0.114 * c[2]) < 140; };

// ── контракт: полный список токенов, который гарантирует buildTokens ────────
export const TOKENS = [
  '--app-bg', '--app-bg-solid', '--app-sidebar',
  '--app-card', '--app-card-grad', '--app-gold-card-grad',
  '--app-text', '--app-sub', '--app-body', '--app-dim', '--app-dimmer',
  '--app-accent', '--app-accent-soft', '--app-accent-grad', '--app-accent-wash',
  '--app-border', '--app-border-soft', '--app-border-gold', '--app-track',
  '--app-good', '--app-bad',
  '--app-head-font', '--app-font', '--app-radius', '--app-anim-play',
];

const SERIF = "'Cormorant Garamond', Georgia, serif";
const SANS = "'Manrope', -apple-system, sans-serif";
const MONO = "'JetBrains Mono', 'SF Mono', Consolas, monospace";

// ── из компактной спеки → полная карта CSS-переменных ───────────────────────
// Всё, что не задано в spec, выводится из базовых цветов: bgSolid / text / accent.
export function buildTokens(spec = {}) {
  const bgSolid = spec.bgSolid || (parseHex(spec.bg) ? spec.bg : '#0d0d10');
  const dark = isDark(bgSolid);
  const line = dark ? '#ffffff' : '#000000';           // цвет «света» для рамок/трека
  const text = spec.text || (dark ? '#ece6d6' : '#16140f');
  const accent = spec.accent || '#c9a24b';
  const accentSoft = spec.accentSoft || mix(accent, '#ffffff', 0.36);
  const card = spec.card || mix(bgSolid, line, dark ? 0.045 : 0.04);
  const cardLo = parseHex(card) ? card : mix(bgSolid, line, dark ? 0.045 : 0.04);

  return {
    '--app-bg': spec.bg || bgSolid,
    '--app-bg-solid': bgSolid,
    '--app-sidebar': spec.sidebar || mix(bgSolid, dark ? '#000000' : '#ffffff', 0.35),
    '--app-card': card,
    '--app-card-grad': spec.cardGrad || `linear-gradient(160deg, ${mix(cardLo, line, 0.02)}, ${bgSolid})`,
    '--app-gold-card-grad': spec.goldCardGrad || `linear-gradient(160deg, ${mix(cardLo, accent, 0.16)}, ${bgSolid})`,
    '--app-text': text,
    '--app-sub': spec.sub || mix(text, bgSolid, 0.42),
    '--app-body': spec.body || mix(text, bgSolid, 0.2),
    '--app-dim': spec.dim || mix(text, bgSolid, 0.55),
    '--app-dimmer': spec.dimmer || mix(text, bgSolid, 0.68),
    '--app-accent': accent,
    '--app-accent-soft': accentSoft,
    '--app-accent-grad': spec.accentGrad || `linear-gradient(135deg, ${accentSoft}, ${accent})`,
    '--app-accent-wash': spec.accentWash || mix(bgSolid, accent, 0.14),
    '--app-border': spec.border || mix(bgSolid, line, dark ? 0.10 : 0.13),
    '--app-border-soft': spec.borderSoft || mix(bgSolid, line, dark ? 0.05 : 0.07),
    '--app-border-gold': spec.borderGold || mix(bgSolid, accent, 0.20),
    '--app-track': spec.track || mix(bgSolid, line, dark ? 0.14 : 0.16),
    '--app-good': spec.good || '#22c55e',
    '--app-bad': spec.bad || '#ef4444',
    '--app-head-font': spec.headFont || SERIF,
    '--app-font': spec.font || SANS,
    '--app-radius': spec.radius || '18px',
    '--app-anim-play': spec.anim === false ? 'paused' : 'running',
  };
}

// ============================================================================
//  РЕЕСТР ТЕМ
//  Полные темы задают всё точно; компактные — пару полей, остальное выводится.
// ============================================================================
export const THEMES = {
  // ── флагман: полностью заданная тема ──────────────────────────────────────
  lux: {
    name: '✦ Люкс-золото', nameKey: 'th_lux',
    bg: 'radial-gradient(120% 100% at 80% -10%,#16151b,#0d0d10 55%)', bgSolid: '#0d0d10', sidebar: '#0a0a0d',
    card: '#121117', cardGrad: 'linear-gradient(160deg,#121216,#0d0d10)', goldCardGrad: 'linear-gradient(160deg,#15140e,#0d0d10)',
    text: '#ece6d6', sub: '#8a8a92', body: '#cfcabb', dim: '#6a6a72', dimmer: '#5a5a62',
    accent: '#c9a24b', accentSoft: '#e8cf8a', accentWash: '#1a160c',
    border: '#1f1f25', borderSoft: '#16161b', borderGold: '#2a2418', track: '#23232a',
    headFont: SERIF, font: SANS,
  },

  // ── Lux Gold 2.0: «тише золото» — слои вместо рамок, тёплые статусные цвета,
  //    поднятый контраст подписей, светокромка карточек вместо тёмной клетки ──
  lux2: {
    name: '✦ Люкс-золото 2.0', nameKey: 'th_lux2',
    bg: 'linear-gradient(178deg,#141109 0%,#0F0D09 34%,#0C0A07 100%)', bgSolid: '#0F0D09', sidebar: '#0B0906',
    card: '#181410', cardGrad: 'linear-gradient(180deg,#181410,#131008)',
    goldCardGrad: 'linear-gradient(160deg,#1E1710,#131008)',
    text: '#F0E8D8', sub: '#A79B82', body: '#DED6C4', dim: '#93876C', dimmer: '#8B8474',
    accent: '#C9A24B', accentSoft: '#E8CF8A', accentWash: '#201909',
    border: 'rgba(240,232,216,.075)', borderSoft: 'rgba(240,232,216,.045)',
    borderGold: 'rgba(201,162,75,.42)', track: '#231C11',
    good: '#7FBF9E', bad: '#D98A6A',
    headFont: SERIF, font: SANS,
    css: `::selection{background:rgba(201,162,75,.32)}
      *::-webkit-scrollbar{width:9px;height:9px}
      *::-webkit-scrollbar-thumb{background:#2A2214;border-radius:5px}
      *::-webkit-scrollbar-track{background:transparent}`,
  },

  // ── Мастерская: свисс-моно рабочий режим — моноширинные данные, жёсткая
  //    сетка, максимум контраста, единственный золотой акцент у важного ──
  mono: {
    iconStyle: { strokeWidth: 1.2, linecap: 'square', glow: 'none' },
    name: '▪ Мастерская', nameKey: 'th_mono',
    bg: '#0A0A0A', bgSolid: '#0A0A0A', sidebar: '#0A0A0A',
    card: '#111110', cardGrad: 'linear-gradient(180deg,#121211,#0E0E0D)',
    goldCardGrad: 'linear-gradient(180deg,#141310,#0E0E0D)',
    text: '#EDEDE8', sub: '#8B887B', body: '#D9D7CE', dim: '#7A776C', dimmer: '#8B887B',
    accent: '#C9A24B', accentSoft: '#DDBE6E', accentWash: '#171308',
    border: '#1C1C1A', borderSoft: '#161614', borderGold: 'rgba(201,162,75,.5)', track: '#1C1C1A',
    good: '#A9C9B4', bad: '#D98A6A',
    headFont: MONO, font: MONO,
    css: `::selection{background:rgba(201,162,75,.3)} body{letter-spacing:.01em}`,
  },

  // ── новые полноценные темы (пример: тема = один объект) ────────────────────
  indigo: {
    name: '✦ Полночь-индиго', nameKey: 'th_indigo',
    bg: 'radial-gradient(120% 100% at 80% -10%,#1a1c30,#0b0c16 55%)', bgSolid: '#0b0c16', sidebar: '#080912',
    card: '#12131f', cardGrad: 'linear-gradient(160deg,#141527,#0b0c16)', goldCardGrad: 'linear-gradient(160deg,#161a3a,#0b0c16)',
    text: '#e7e9f6', sub: '#878cab', body: '#c5c9e2', dim: '#6b7090', dimmer: '#565a7a',
    accent: '#7c8cff', accentSoft: '#aab6ff', accentWash: '#15172e',
    border: '#22243a', borderSoft: '#161829', borderGold: '#2b3058', track: '#22243a',
    headFont: SERIF, font: SANS,
  },
  emerald: {
    name: '✦ Изумруд', nameKey: 'th_emerald',
    bg: 'radial-gradient(120% 100% at 80% -10%,#0e1f18,#05100b 55%)', bgSolid: '#05100b', sidebar: '#040d09',
    card: '#0c1813', cardGrad: 'linear-gradient(160deg,#0e1d16,#05100b)', goldCardGrad: 'linear-gradient(160deg,#0e2418,#05100b)',
    text: '#e3f3ea', sub: '#7fa692', body: '#bfd9cb', dim: '#5f8773', dimmer: '#4c6c5c',
    accent: '#34d399', accentSoft: '#6ee7b7', accentWash: '#0c2018',
    border: '#1b2c24', borderSoft: '#122019', borderGold: '#1b3a2a', track: '#1d3128',
    headFont: SERIF, font: SANS,
  },
  rose: {
    name: '✦ Розовое золото', nameKey: 'th_rose',
    bg: 'radial-gradient(120% 100% at 80% -10%,#241519,#13090c 55%)', bgSolid: '#13090c', sidebar: '#0f0709',
    card: '#1b1014', cardGrad: 'linear-gradient(160deg,#1d1216,#13090c)', goldCardGrad: 'linear-gradient(160deg,#231411,#13090c)',
    text: '#f3e3e6', sub: '#a8838c', body: '#dcc6cb', dim: '#8a6a72', dimmer: '#6a525a',
    accent: '#d9a36b', accentSoft: '#f0cda0', accentWash: '#1f130d',
    border: '#2a1e22', borderSoft: '#1d1418', borderGold: '#3a281c', track: '#2c2024',
    headFont: SERIF, font: SANS,
  },

  // ── пример темы с СОБСТВЕННОЙ анимацией фона (поле css) ────────────────────
  aurora: {
    name: '✦ Сияние', nameKey: 'th_aurora',
    bg: 'linear-gradient(135deg,#021024,#0a3d62 45%,#0a8270)', bgSolid: '#021024', sidebar: '#020b18',
    text: '#e6fff7', accent: '#36e0c0', accentSoft: '#9bffe9',
    css: `
      html{ background-size:220% 220% !important; animation: themeAuroraShift 22s ease-in-out infinite alternate; }
      @keyframes themeAuroraShift { 0%{background-position:0% 30%} 100%{background-position:100% 70%} }
    `,
  },

  // ── Космос: глубокий космос, мерцающие звёзды + дрейф туманности ──────────
  cosmos: {
    menuStyle: { shape: 'dot', letterSpacing: '.04em', uppercase: false }, loader: { kind: 'stars', label: 'Прокладываю курс' },
    iconStyle: { strokeWidth: 1.3, linecap: 'round', glow: '0 0 5px rgba(107,140,255,.8)' },
    name: '🌌 Космос', nameKey: 'th_cosmos',
    bg: 'radial-gradient(120% 100% at 70% -5%,#2a2455,#0c1030 42%,#060818 82%)', bgSolid: '#060818', sidebar: '#05060f',
    card: '#111633', cardGrad: 'linear-gradient(160deg,#141a3e,#060818)', goldCardGrad: 'linear-gradient(160deg,#171d52,#060818)',
    text: '#eef0ff', sub: '#8a92c4', body: '#c8ccf0', dim: '#6a72a8', dimmer: '#565e8c',
    accent: '#6b8cff', accentSoft: '#43e8d8', accentWash: '#101a3a',
    border: '#20264a', borderSoft: '#161a34', borderGold: '#2b3a7a', track: '#20264a',
    headFont: SERIF, font: SANS,
    css: `
      html{ background-size:180% 180% !important; animation: thCosmosDrift 46s ease-in-out infinite alternate; }
      @keyframes thCosmosDrift { 0%{background-position:22% 18%} 100%{background-position:78% 62%} }
      html::after{ content:''; position:fixed; inset:0; z-index:-1; pointer-events:none;
        background-image:radial-gradient(1.5px 1.5px at 15% 25%,#fff,transparent),radial-gradient(1.5px 1.5px at 75% 18%,#fff,transparent),radial-gradient(2px 2px at 45% 62%,#cfe0ff,transparent),radial-gradient(1.5px 1.5px at 85% 75%,#fff,transparent),radial-gradient(1.5px 1.5px at 30% 85%,#fff,transparent),radial-gradient(1px 1px at 60% 40%,#fff,transparent),radial-gradient(1px 1px at 8% 58%,#bfe0ff,transparent);
        animation: thCosmosTw 5s ease-in-out infinite; }
      @keyframes thCosmosTw { 0%,100%{opacity:.2} 50%{opacity:.62} }
    `,
  },

  // ── Жидкий хром: тёмное серебро, ползущий перламутровый блик ──────────────
  chrome: {
    menuStyle: { shape: 'pill', letterSpacing: '.02em', uppercase: false }, loader: { kind: 'sweep', label: 'Полировка' },
    iconStyle: { strokeWidth: 1.4, linecap: 'round', glow: '0 0 4px rgba(192,168,255,.55)' },
    name: '🪞 Жидкий хром', nameKey: 'th_chrome',
    bg: 'radial-gradient(120% 100% at 80% -10%,#17171f,#0a0a0f 58%)', bgSolid: '#0a0a0f', sidebar: '#08080c',
    card: '#14141c', cardGrad: 'linear-gradient(160deg,#17171f,#0a0a0f)', goldCardGrad: 'linear-gradient(160deg,#1b1a26,#0a0a0f)',
    text: '#eceefa', sub: '#8a92b8', body: '#cfd2ea', dim: '#6a72a0', dimmer: '#565e84',
    accent: '#c0a8ff', accentSoft: '#7fd0e8', accentWash: '#16141f',
    border: '#22232e', borderSoft: '#17171f', borderGold: '#33324a', track: '#22232e',
    headFont: SANS, font: SANS,
    css: `
      html::before{ content:''; position:fixed; inset:0; z-index:-1; pointer-events:none;
        background:linear-gradient(115deg,transparent 40%,rgba(200,210,255,.10) 50%,transparent 60%); background-size:280% 280%;
        animation: thChromeSweep 9s linear infinite; }
      @keyframes thChromeSweep { 0%{background-position:0% 0%} 100%{background-position:200% 200%} }
    `,
  },

  // ── Кибер HUD: неоновая сетка + сканлайн, моно-типографика ────────────────
  cyberhud: {
    menuStyle: { shape: 'bracket', letterSpacing: '.12em', uppercase: true }, loader: { kind: 'boot', label: 'INITIALIZING SYSTEM', mono: true },
    iconStyle: { strokeWidth: 1.1, linecap: 'square', glow: '0 0 6px rgba(34,211,238,.9)' },
    name: '🖥 Кибер HUD', nameKey: 'th_cyberhud',
    bg: '#05070d', bgSolid: '#05070d', sidebar: '#04060b',
    card: '#0a1420', cardGrad: 'linear-gradient(180deg,#0a1622,#0a1420)', goldCardGrad: 'linear-gradient(180deg,#0c1a28,#0a1420)',
    text: '#e6faff', sub: '#3f8fa0', body: '#9fd4df', dim: '#3f6f7a', dimmer: '#356069',
    accent: '#22d3ee', accentSoft: '#39ff14', accentWash: '#07202a',
    border: '#14323f', borderSoft: '#0e2028', borderGold: 'rgba(34,211,238,.5)', track: '#0e1c28',
    good: '#39ff14', bad: '#ff3b5c',
    headFont: MONO, font: MONO, radius: '10px',
    css: `
      html::before{ content:''; position:fixed; inset:0; z-index:-1; pointer-events:none;
        background-image:linear-gradient(#0e1c2855 1px,transparent 1px),linear-gradient(90deg,#0e1c2855 1px,transparent 1px); background-size:28px 28px; }
      html::after{ content:''; position:fixed; left:0; right:0; top:0; height:90px; z-index:-1; pointer-events:none;
        background:linear-gradient(#22d3ee00,#22d3ee22,#22d3ee00); animation: thCyberScan 6s linear infinite; }
      @keyframes thCyberScan { 0%{transform:translateY(-100px)} 100%{transform:translateY(100vh)} }
    `,
  },

  // ── Крылья: светлая иридесценция, парящие цветные ореолы ───────────────────
  wings: {
    menuStyle: { shape: 'soft', letterSpacing: '.01em', uppercase: false }, loader: { kind: 'orbs', label: 'Расправляю крылья' },
    iconStyle: { strokeWidth: 1.8, linecap: 'round', glow: 'none' },
    name: '🪽 Крылья', nameKey: 'th_wings',
    bg: 'linear-gradient(180deg,#e8f2ff,#eff0ff 30%,#f5edff 55%,#ffeff3 78%,#fff6e9)', bgSolid: '#eef2ff', sidebar: '#e4ecfb',
    card: '#ffffff', cardGrad: 'linear-gradient(160deg,#ffffff,#f2f4ff)', goldCardGrad: 'linear-gradient(160deg,#ffffff,#f3edff)',
    text: '#3a3a6a', sub: '#7d7ea6', body: '#4c4c78', dim: '#9a9ac0', dimmer: '#b4b4d0',
    accent: '#6aa8ff', accentSoft: '#b48cff', accentWash: '#eaf0ff',
    border: '#dcd8f0', borderSoft: '#e8e4f6', borderGold: '#cfd8ff', track: '#e4e0f4',
    good: '#3aa876', bad: '#d9756b',
    headFont: SERIF, font: SANS,
    css: `
      html::after{ content:''; position:fixed; inset:0; z-index:-1; pointer-events:none;
        background:radial-gradient(220px 180px at 12% 15%,rgba(143,192,255,.36),transparent 65%),radial-gradient(220px 190px at 88% 28%,rgba(202,160,255,.32),transparent 65%),radial-gradient(190px 170px at 30% 85%,rgba(255,200,143,.30),transparent 65%);
        animation: thWingsFloat 15s ease-in-out infinite alternate; }
      @keyframes thWingsFloat { 0%{transform:translate(0,0)} 100%{transform:translate(12px,-14px)} }
    `,
  },

  // ── компактные темы: задано минимум, остальное выводится ───────────────────
  dark:    { name: 'Тёмная', nameKey: 'th_dark', bg: '#0d0d10', card: '#15161c', text: '#ece6d6', accent: '#c9a24b' },
  midnight:{ name: 'Полночь-классик', nameKey: 'th_midnight', bg: '#0a0e1a', card: '#111827', text: '#e8eaf0', accent: '#6c8cff' },
  forest:  { name: 'Лес', nameKey: 'th_forest', bg: '#0a140f', card: '#0f1d16', text: '#e4efe8', accent: '#3fb37a' },
  wine:    { name: 'Вино', nameKey: 'th_wine', bg: '#140a0e', card: '#1d0f15', text: '#f0e4e8', accent: '#c75c7a' },
  light:   { name: 'Светлая', nameKey: 'th_light', bg: '#f5f5f0', card: '#ffffff', text: '#1a1a1a', accent: '#b58a2e' },
  paper:   { name: 'Бумага', nameKey: 'th_paper', bg: '#ece5d8', card: '#f7f1e6', text: '#2a2018', accent: '#9a6a2e' },
  ocean:   { name: '🌊 Океан', nameKey: 'th_ocean', bg: 'linear-gradient(160deg,#0f2027,#203a43 50%,#2c5364)', bgSolid: '#0f2027', text: '#e0f7fa', accent: '#4dd0e1' },
  cyber:   { name: '⚡ Кибер', nameKey: 'th_cyber', bg: 'linear-gradient(160deg,#0d0221,#190b38 50%,#2a0a4a)', bgSolid: '#0d0221', text: '#f0e0ff', accent: '#ff4da6', accentSoft: '#ff9ad0' },
  matrix:  { name: '💚 Матрица', nameKey: 'th_matrix', bg: '#000800', bgSolid: '#000800', card: '#031203', text: '#39ff85', accent: '#22ff66', accentSoft: '#9dffc1' },
  gold:    { name: '👑 Золото', nameKey: 'th_gold', bg: 'linear-gradient(160deg,#1a1200,#2a1f00 50%,#3a2a00)', bgSolid: '#1a1200', text: '#ffd700', accent: '#ffcf33', accentSoft: '#ffe79a' },
};

export const DEFAULT_THEME = 'lux2';

// ── Theme Studio helpers ────────────────────────────────────────────────────
// Background animation presets the in-app editor can attach to a theme (`css`).
export const BG_FX = {
  none:    { label: 'Нет',      css: '' },
  aurora:  { label: 'Сияние',   css: `html{background-size:220% 220% !important;animation:tFxShift 22s ease-in-out infinite alternate}@keyframes tFxShift{0%{background-position:0% 30%}100%{background-position:100% 70%}}` },
  pulse:   { label: 'Пульс',    css: `html{animation:tFxPulse 9s ease-in-out infinite}@keyframes tFxPulse{0%,100%{filter:none}50%{filter:brightness(1.07)}}` },
  drift:   { label: 'Дрейф',    css: `html{background-size:160% 160% !important;animation:tFxDrift 40s linear infinite}@keyframes tFxDrift{0%{background-position:0% 0%}100%{background-position:100% 100%}}` },
  // Overlay effects — visible on ANY background (solid or gradient), sit behind content.
  twinkle: { label: 'Звёзды',   css: `html::after{content:'';position:fixed;inset:0;z-index:-1;pointer-events:none;background-image:radial-gradient(1.5px 1.5px at 15% 25%,#fff,transparent),radial-gradient(1.5px 1.5px at 75% 18%,#fff,transparent),radial-gradient(2px 2px at 45% 62%,#fff,transparent),radial-gradient(1.5px 1.5px at 85% 75%,#fff,transparent),radial-gradient(1.5px 1.5px at 30% 85%,#fff,transparent),radial-gradient(1px 1px at 60% 40%,#fff,transparent),radial-gradient(1px 1px at 8% 58%,#fff,transparent);animation:tFxTwinkle 5s ease-in-out infinite}@keyframes tFxTwinkle{0%,100%{opacity:.18}50%{opacity:.6}}` },
  sweep:   { label: 'Перелив',  css: `html::before{content:'';position:fixed;inset:0;z-index:-1;pointer-events:none;background:linear-gradient(115deg,transparent 42%,rgba(255,255,255,.07) 50%,transparent 58%);background-size:280% 280%;animation:tFxSweep 11s linear infinite}@keyframes tFxSweep{0%{background-position:0% 0%}100%{background-position:200% 200%}}` },
  vignette:{ label: 'Виньетка', css: `html::before{content:'';position:fixed;inset:0;z-index:-1;pointer-events:none;background:radial-gradient(circle at 50% 38%,transparent 52%,rgba(0,0,0,.42));animation:tFxVignette 8s ease-in-out infinite}@keyframes tFxVignette{0%,100%{opacity:.35}50%{opacity:.75}}` },
};
export const detectFx = (css) => {
  if (!css) return 'none';
  if (css.includes('tFxShift')) return 'aurora';
  if (css.includes('tFxPulse')) return 'pulse';
  if (css.includes('tFxDrift')) return 'drift';
  if (css.includes('tFxTwinkle')) return 'twinkle';
  if (css.includes('tFxSweep')) return 'sweep';
  if (css.includes('tFxVignette')) return 'vignette';
  return 'none';
};

// Editor state { name, base, accent, card, text, radius, headFont, font, fx }
// → a full theme spec (buildTokens fills in everything else from these primitives).
export function composeTheme(e = {}) {
  const base = e.base || '#0d0d10';
  const dark = isDark(base);
  const bg = dark
    ? `radial-gradient(120% 100% at 80% -10%, ${mix(base, '#ffffff', 0.05)}, ${base} 60%)`
    : base;
  return {
    name: e.name || 'Моя тема',
    bg, bgSolid: base,
    accent: e.accent || '#c9a24b',
    accentSoft: mix(e.accent || '#c9a24b', '#ffffff', 0.36),
    card: e.card, text: e.text,
    radius: (e.radius != null ? e.radius : 18) + 'px',
    headFont: e.headFont, font: e.font,
    css: (BG_FX[e.fx] && BG_FX[e.fx].css) || '',
    custom: true,
  };
}
